import React, { useEffect, useState } from "react";
import { adminApi } from "../../utils/adminApi";
import { paths } from "../../paths";
import { getCache, setCache, isFresh, invalidate } from "./adminCache";
import {
  AdminShell,
  Panel,
  Modal,
  ResultModal,
  StatCard,
  inputCls,
  labelCls,
  tierFor,
  TIER_CLS,
  Loading,
} from "./adminUi";

const ManageCAs = () => {
  const [cas, setCAs] = useState(() => getCache("cas") || []);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [editPoints, setEditPoints] = useState("");
  const [loading, setLoading] = useState(() => !getCache("cas"));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // popup after a points adjustment
  const [pool, setPool] = useState(0); // total points available across live tasks (tier denominator)

  const fetchCAs = async (q = "") => {
    try {
      const res = await adminApi.get("/cas", { params: q ? { q } : {} });
      setCAs(res.data || []);
      if (!q) setCache("cas", res.data || []); // only the unfiltered list is cacheable
    } catch (err) {
      console.error("Load CAs error:", err);
    } finally {
      setLoading(false);
    }
  };

  // The tier denominator (sum of live task points) lives on /stats — fetch it
  // once so tier badges reflect each CA's share of the available pool.
  useEffect(() => {
    adminApi
      .get("/stats")
      .then((res) => setPool(res.data?.totalAvailablePoints || 0))
      .catch(() => {});
  }, []);

  // Single debounced effect — serves the base list from cache and only hits the
  // backend when the cache is stale or the user is searching.
  useEffect(() => {
    if (!search) {
      const cached = getCache("cas");
      if (cached) setCAs(cached);
      if (isFresh("cas")) { setLoading(false); return; }
    }
    const t = setTimeout(() => fetchCAs(search), search ? 350 : 0);
    return () => clearTimeout(t);
  }, [search]);

  const viewDetails = async (id) => {
    const res = await adminApi.get(`/cas/${id}`);
    setSelected(res.data);
    setEditPoints(res.data.points);
  };

  const savePoints = async () => {
    const target = selected;
    const prev = selected.points || 0;
    // Number("") === 0, so a cleared field would silently zero the CA's score.
    // Treat an empty/invalid input as a no-op rather than a real 0.
    const next = editPoints === "" ? NaN : Number(editPoints);
    if (Number.isNaN(next) || next < 0) {
      alert("Enter a valid points value (0 or greater).");
      return;
    }
    setBusy(true);
    try {
      await adminApi.patch(`/cas/${selected.id}/points`, { points: next });
      setSelected(null);
      invalidate("cas", "leaderboard", "dashboard");
      fetchCAs(search);
      setResult({ name: target.name, prev, next });
    } catch (err) {
      alert(err.response?.data?.message || "Could not update points");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loading label="Loading CAs..." />;

  const ranked = [...cas].sort((a, b) => (b.points || 0) - (a.points || 0));
  const totalPoints = cas.reduce((s, c) => s + (c.points || 0), 0);
  const goldCount = cas.filter((c) => tierFor(c.points || 0, pool) === "GOLD").length;

  return (
    <AdminShell
      tab={paths.adminCas}
      title="Manage CAs"
      subtitle="View Campus Ambassadors, inspect their submissions, and adjust their points."
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pl-2">
        <StatCard label="Total CA's" value={cas.length} sub="registered ambassadors" />
        <StatCard label="Gold Tier" value={goldCount} sub="≥80% of total pts" />
        <StatCard label="Pts Awarded" value={totalPoints.toLocaleString()} sub="across all CA's" />
      </div>

      <Panel title="Campus Ambassadors" sub={`${cas.length} registered · search by name or college`}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, college, or email..."
          className="w-full bg-white border border-black/10 rounded-xl px-4 py-3 text-sm mb-3 focus:outline-none focus:border-[#D15B28] transition"
        />
        <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
          <table className="w-full text-left border-collapse">
            <thead className="sticky top-0 bg-[#F5EFEB] z-10">
              <tr className="text-[10px] font-extrabold tracking-wider text-[#2B1B17]/40 uppercase">
                <th className="py-2 px-3 w-14">Rank</th>
                <th className="py-2 px-3">Name</th>
                <th className="py-2 px-3">Email</th>
                <th className="py-2 px-3">College</th>
                <th className="py-2 px-3 text-right">Points</th>
                <th className="py-2 px-3">Tier</th>
                <th className="py-2 px-3 w-full"></th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {ranked.length === 0 && (
                <tr><td colSpan={7} className="text-center text-black/40 py-6">No CAs found.</td></tr>
              )}
              {ranked.map((ca, i) => {
                const tier = tierFor(ca.points || 0, pool);
                return (
                  <tr key={ca.id} className="border-b border-black/[0.03] last:border-none">
                    <td className="py-2.5 px-3 font-black text-black/50">#{i + 1}</td>
                    <td className="py-2.5 px-3 font-bold text-[#2B1B17] whitespace-nowrap">{ca.name}</td>
                    <td className="py-2.5 px-3 text-black/50 whitespace-nowrap">{ca.email}</td>
                    <td className="py-2.5 px-3 text-black/50 whitespace-nowrap">{ca.college}</td>
                    <td className="py-2.5 px-3 text-right font-black text-[#2B1B17]">{(ca.points || 0).toLocaleString()}</td>
                    <td className="py-2.5 px-3">
                      <span className={`text-[9px] font-extrabold tracking-wider px-2 py-1 rounded-md ${TIER_CLS[tier]}`}>{tier}</span>
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <button onClick={() => viewDetails(ca.id)} className="text-xs font-bold text-[#D15B28] hover:opacity-80 whitespace-nowrap">View →</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      {selected && (
        <Modal label="Campus Ambassador" title={selected.name} onClose={() => setSelected(null)} wide>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Email" value={selected.email} />
            <Field label="Mobile" value={selected.mobile} />
            <Field label="College" value={selected.college} />
            <Field label="Referrals" value={selected.referralCount} />
            <Field label="Referral Code" value={selected.referralCode} />
            <div>
              <p className={labelCls}>Points</p>
              <input
                type="number"
                value={editPoints}
                onChange={(e) => setEditPoints(e.target.value)}
                className={`${inputCls} mt-1.5`}
              />
            </div>
          </div>

          {selected.submissions?.length > 0 && (
            <div className="mt-5">
              <p className={labelCls}>Submissions</p>
              <div className="bg-white border border-black/5 rounded-lg mt-1.5 overflow-x-auto max-h-[260px] overflow-y-auto">
                <table className="w-full text-left border-collapse text-sm">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="text-[10px] font-extrabold tracking-wider text-[#2B1B17]/40 uppercase">
                      <th className="py-2 px-3">Task</th>
                      <th className="py-2 px-3">Status</th>
                      <th className="py-2 px-3 text-right">Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.submissions.map((s) => (
                      <tr key={s.id} className="border-t border-black/[0.04]">
                        <td className="py-2 px-3 font-medium text-[#2B1B17]">{s.Task?.header}</td>
                        <td className="py-2 px-3 capitalize text-black/50">{s.status}</td>
                        <td className="py-2 px-3 text-right font-bold text-green-600">{s.pointsAwarded || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 mt-6">
            <button onClick={savePoints} disabled={busy} className="flex-1 bg-[#D15B28] text-white font-semibold py-3.5 rounded-full hover:bg-[#b04a1f] transition disabled:opacity-60">
              {busy ? "Saving..." : "Save Points ➜"}
            </button>
            <button onClick={() => setSelected(null)} className="px-7 py-3.5 rounded-full bg-black/5 hover:bg-black/10 transition font-semibold text-[#2B1B17]">
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {result && (() => {
        const delta = result.next - result.prev;
        const sign = delta >= 0 ? "+" : "";
        return (
          <ResultModal
            accent="peach"
            title="Points Updated!"
            message={`${result.name}'s total adjusted from ${result.prev.toLocaleString()} → ${result.next.toLocaleString()} pts.`}
            chip={`${sign}${delta.toLocaleString()} pts`}
            primary={{ label: "Done", onClick: () => setResult(null) }}
            onClose={() => setResult(null)}
          />
        );
      })()}
    </AdminShell>
  );
};

const Field = ({ label, value }) => (
  <div>
    <p className={labelCls}>{label}</p>
    <p className="text-sm font-semibold text-[#2B1B17] mt-1 break-words">{value || "—"}</p>
  </div>
);

export default ManageCAs;
